import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheet } from '@angular/material/bottom-sheet';
import { CsIconModule } from '@components/icon';
import { EventLoggerService } from '@services/event-logger-service';
import { ReorderKitDetail, ReorderKitProduct } from '@cureskin/api-client/src/server-api';
import { PRICE_DETAILS_BOTTOMSHEET_TYPE, PriceDetailsBottomsheetType } from '../../../app.constants';
import { AppConfig } from '../../../app.config';

export interface RegimenReorderPriceDetailsData {
  type: typeof PRICE_DETAILS_BOTTOMSHEET_TYPE.REGIMEN_REORDER;
  regimenDetails: ReorderKitDetail;
  cashUsed: number;
  originalDiscount: number;
}

export interface RegimenPriceDetailsData {
  type: typeof PRICE_DETAILS_BOTTOMSHEET_TYPE.REGIMEN;
  fixedPriceMrp: number;
  fixedPrice: number;
}

export type PriceDetailsType = PriceDetailsBottomsheetType;

export type PriceDetailsBottomsheetData = RegimenReorderPriceDetailsData | RegimenPriceDetailsData;

@Component({
  selector: 'app-price-details-bottomsheet',
  standalone: true,
  imports: [NgIf, NgFor, CsIconModule, NgTemplateOutlet],
  templateUrl: './price-details-bottomsheet.component.html',
})
export class PriceDetailsBottomsheetComponent implements OnInit {

  data: PriceDetailsBottomsheetData = inject(MAT_BOTTOM_SHEET_DATA);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly eventLoggerService = inject(EventLoggerService);
  readonly appConfig = inject(AppConfig);

  productsTotalMrp: number = 0;
  cashUsed: number = 0;
  originalDiscount: number = 0;
  discount: number = 0;

  ngOnInit(): void {
    if (this.data.type === this.appConfig.Shared.priceDetailsBottomsheetType.REGIMEN_REORDER) {
      this.handleRegimenReorderData(this.data);
      return;
    }

    this.handleRegimenData(this.data);
  }

  private handleRegimenReorderData(data: RegimenReorderPriceDetailsData): void {
    this.eventLoggerService.cleverTapEvent('impression', JSON.stringify({
      name: 'regimen_reorder_price_details_sheet',
      totalMrp: data.regimenDetails.totalMrp,
      totalSP: data.regimenDetails.totalSP,
      discountInRupees: data.regimenDetails.discountInRupees,
      regimenClass: data.regimenDetails.regimenClass
    }));
    this.productsTotalMrp = data.regimenDetails.products.reduce(
      (acc: number, product: ReorderKitProduct) => acc + product.mrp, 0
    );
    this.cashUsed = data.cashUsed || 0;
    this.originalDiscount = data.originalDiscount || 0;
  }

  private handleRegimenData(data: RegimenPriceDetailsData): void {
    this.eventLoggerService.cleverTapEvent('impression', JSON.stringify({
      name: 'regimen_price_details_sheet',
      fixedPriceMrp: data.fixedPriceMrp,
      fixedPrice: data.fixedPrice,
    }));
    this.discount = data.fixedPriceMrp - data.fixedPrice;
  }

  closeBottomSheet(): void {
    this.bottomSheet.dismiss();
  }

  get regimenReorderTemplateContext(): { $implicit: RegimenReorderPriceDetailsData } | null {
    if (this.data.type !== this.appConfig.Shared.priceDetailsBottomsheetType.REGIMEN_REORDER) {
      return null;
    }

    return { $implicit: this.data };
  }

  get regimenTemplateContext(): { $implicit: RegimenPriceDetailsData } | null {
    if (this.data.type !== PRICE_DETAILS_BOTTOMSHEET_TYPE.REGIMEN) {
      return null;
    }

    return { $implicit: this.data };
  }
}
